// components/TransfertTitres/ConfirmTransaction/ConfirmationModal.jsx
import { useState, useEffect } from 'react';
import { Modal, Button, Alert, Form } from 'react-bootstrap';
import { FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaTimes, FaPlusCircle, FaMinusCircle } from 'react-icons/fa';

const ConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  title = "Confirmation requise",
  message = "Êtes-vous sûr de vouloir continuer ?",
  confirmText = "Confirmer",
  cancelText = "Annuler",
  variant = "warning", // 'warning', 'danger', 'info', 'success'
  isLoading = false,
  isSuccess = false,
  isError = false,
  errorMessage = "",
  additionalInfo = "",
  requiresAcknowledgment = false,
  acknowledgmentText = "Je comprends que cette action est irréversible",
  showCloseButton = true,
  // Nouvelles props pour personnaliser les boutons
  confirmButtonVariant = null, // Permet de surcharger la variante du bouton
  confirmButtonIcon = null, // Icone personnalisée pour le bouton
  showOperationIcons = false, // Afficher les icônes d'opération spécifiques
  operationType = null // 'add' ou 'remove' pour les transferts
}) => {
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // Réinitialiser l'état quand le modal s'ouvre/ferme
  useEffect(() => {
    if (show) {
      setIsAcknowledged(false);
    }
  }, [show]);

  const getVariantIcon = () => {
    switch (variant) {
      case 'danger':
        return <FaExclamationTriangle className="text-danger me-2" />;
      case 'success':
        return <FaCheckCircle className="text-success me-2" />;
      case 'info':
        return <FaInfoCircle className="text-info me-2" />;
      case 'warning':
      default:
        return <FaExclamationTriangle className="text-warning me-2" />;
    }
  };

  const getVariantClass = () => {
    // Si une variante de bouton est spécifiée, l'utiliser
    if (confirmButtonVariant) {
      return `btn-${confirmButtonVariant}`;
    }
    
    // Sinon, utiliser la variante par défaut
    switch (variant) {
      case 'danger':
        return 'btn-danger';
      case 'success':
        return 'btn-success';
      case 'info':
        return 'btn-info';
      case 'warning':
      default:
        return 'btn-warning';
    }
  };

  const getConfirmButtonIcon = () => {
    if (confirmButtonIcon) {
      return confirmButtonIcon;
    }
    
    if (showOperationIcons && operationType) {
      return operationType === 'add' ? 
        <FaPlusCircle className="me-2" /> : 
        <FaMinusCircle className="me-2" />;
    }
    
    return null;
  };

  const handleConfirm = () => {
    // Vérifier si la case doit être cochée
    if (requiresAcknowledgment && !isAcknowledged) {
      return; // Ne rien faire si non coché
    }
    onConfirm();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop={isLoading ? "static" : true}
      keyboard={!isLoading}
      size="md"
      className="confirmation-modal"
      dialogClassName="modal-dialog-centered modal-dialog-top" // Ajoutez cette classe
      contentClassName="slide-from-top" 
    >
      <Modal.Header className={`border-0 pb-0 ${variant === 'danger' ? 'border-danger' : ''}`}>
        <Modal.Title className="d-flex align-items-center w-100">
          {getVariantIcon()}
          <span className="flex-grow-1">{title}</span>
          {showCloseButton && !isLoading && (
            <Button
              variant="link"
              onClick={onHide}
              className="p-0 text-muted"
              disabled={isLoading}
            >
              <FaTimes />
            </Button>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="pt-3">
        {isError && (
          <Alert variant="danger" className="mb-3">
            <FaExclamationTriangle className="me-2" />
            {errorMessage || "Une erreur est survenue"}
          </Alert>
        )}
        
        {isSuccess && (
          <Alert variant="success" className="mb-3">
            <FaCheckCircle className="me-2" />
            Action effectuée avec succès
          </Alert>
        )}
        
        <div className="mb-3">
          <p className="mb-2 whitespace-pre-line">{message}</p>
          {additionalInfo && (
            <div className="alert alert-light border mt-3">
              <small>{additionalInfo}</small>
            </div>
          )}
        </div>
        
        {requiresAcknowledgment && (
          <Form.Group className="mb-3">
            <Form.Check 
              type="checkbox"
              id="acknowledgmentCheckbox"
              label={acknowledgmentText}
              checked={isAcknowledged}
              onChange={(e) => setIsAcknowledged(e.target.checked)}
              className="form-check"
            />
          </Form.Group>
        )}
      </Modal.Body>
      
      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={isLoading}
          className="px-4"
        >
          {cancelText}
        </Button>
        
        <Button
          variant={getVariantClass()}
          onClick={handleConfirm}
          disabled={isLoading || (requiresAcknowledgment && !isAcknowledged)}
          className="px-4 d-flex align-items-center"
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Traitement...
            </>
          ) : (
            <>
              {getConfirmButtonIcon()}
              {confirmText}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const TransactionConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  transactionType,
  quantity,
  securityName,
  fromAccount,
  toAccount,
  isLoading = false
}) => {
  const title = <span className='text text-white pb-1 p-1'>Confirmation de transaction</span>;
  
  const getTransactionMessage = () => {
    let baseMessage = `Vous êtes sur le point de créer une transaction de type "${transactionType}".`;
    baseMessage += `\nQuantité : ${quantity} titres`;
    
    if (securityName) {
      baseMessage += `\nTitre : ${securityName}`;
    }
    
    if (fromAccount) {
      baseMessage += `\nCompte source : ${fromAccount}`;
    }
    
    if (toAccount) {
      baseMessage += `\nCompte destination : ${toAccount}`;
    }
    
    return baseMessage;
  };

  return (
    <ConfirmationModal
      show={show}
      onHide={onHide}
      onConfirm={onConfirm}
      title={title}
      message={getTransactionMessage()}
      confirmText="Valider la transaction"
      cancelText="Annuler"
      variant="warning"
      isLoading={isLoading}
      requiresAcknowledgment={true}
      acknowledgmentText="Je comprends que cette transaction est irréversible et sera enregistrée définitivement"
      additionalInfo="Cette action ne peut pas être annulée. Vérifiez toutes les informations avant de confirmer."
      showCloseButton={!isLoading}
    />
  );
};



export default ConfirmationModal;